import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type AgentUiTemplateResolvedRow,
  fetchAgentUiTemplates,
  getAgentUiTemplateSections,
  resolveAgentUiTemplateRows,
  resolveAgentUiTemplateSuggestionTexts,
} from './agent-ui-templates';

/** Align with agent catalog cadence; bump ``cacheVersion`` (or env mirror) to bust sooner. */
const STALE_MS = 1000 * 60 * 60 * 6;
/** Keep inactive entries a bit past ``staleTime`` so reopening the drawer does not always cold-start. */
const GC_MS = 1000 * 60 * 60 * 12;

export const agentUiTemplatesQueryKey = ['agent-ui-templates'] as const;

export type UseAgentUiTemplatesQueryOptions = {
  enabled?: boolean;
  useCase: string;
  getAuthHeaders: () => Promise<HeadersInit | undefined>;
  subscriptionTier?: string | null;
  /**
 * Optional segment for the React Query key (e.g. organization id) so templates refetch when
 * auth/org scope changes; requests already vary via ``getAuthHeaders`` but the query key must too.
 */
  cacheScopeKey?: string;
  /**
 * Optional segment for the React Query key. Set to the same value as the agent catalog
 * ``UI_TEMPLATES_VERSION`` / response ``version`` (e.g. ``import.meta.env.VITE_AGENT_UI_TEMPLATES_VERSION``)
 * so deploys can bust cache before ``staleTime`` elapses. Omit when unused.
 */
  cacheVersion?: string | number | null;
};

export function useAgentUiTemplatesQuery(viteApiUrl: string | undefined, options: UseAgentUiTemplatesQueryOptions) {
  const { enabled, useCase, getAuthHeaders, subscriptionTier, cacheVersion, cacheScopeKey = '' } = options;
  const tierKey = subscriptionTier?.trim() ?? '';
  const cacheKey =
    cacheVersion !== undefined && cacheVersion !== null && String(cacheVersion).trim() !== ''
      ? String(cacheVersion).trim()
      : '';
  return useQuery({
    queryKey: [...agentUiTemplatesQueryKey, useCase, tierKey, cacheKey, cacheScopeKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      return fetchAgentUiTemplates(viteApiUrl, {
        useCase,
        headers,
        subscriptionTier,
      });
    },
    staleTime: STALE_MS,
    gcTime: GC_MS,
    enabled: enabled ?? true,
    retry: 1,
  });
}

export type UseLaneUiTemplatesOptions = Omit<UseAgentUiTemplatesQueryOptions, 'useCase'>;

export type AgentLaneSuggestionChipsState = {
  /** Chip labels; ``undefined`` until the first successful parse (or while revalidating with no prior data). */
  suggestions: string[] | undefined;
    resolvedTemplateRows: AgentUiTemplateResolvedRow[] | undefined;
  /** True while the first fetch is in flight (query enabled and no ``data`` yet). */
  isSuggestionsLoading: boolean;
  isSuggestionsError: boolean;
  refetchSuggestions: () => void;
};

export function useMonitoringOrgEmptySuggestionTexts(
  viteApiUrl: string | undefined,
  organizationName: string | undefined,
  options: UseLaneUiTemplatesOptions,
): AgentLaneSuggestionChipsState {
  const label = organizationName ?? 'this organization';
  const enabled = options.enabled ?? true;
  const q = useAgentUiTemplatesQuery(viteApiUrl, { ...options, useCase: 'monitoring' });
  const suggestions = useMemo(() => {
    if (!q.data) return undefined;
    const sections = getAgentUiTemplateSections(q.data, 'monitoring', 'organization');
    return resolveAgentUiTemplateSuggestionTexts(sections, { organization_label: label });
  }, [q.data, label]);
  const resolvedTemplateRows = useMemo(() => {
    if (!q.data) return undefined;
    const sections = getAgentUiTemplateSections(q.data, 'monitoring', 'organization');
    return resolveAgentUiTemplateRows(sections, { organization_label: label });
  }, [q.data, label]);
  const isSuggestionsLoading = enabled && q.isFetching && q.data === undefined && !q.isError;
  return {
    suggestions,
    resolvedTemplateRows,
    isSuggestionsLoading,
    isSuggestionsError: q.isError,
    refetchSuggestions: () => {
      void q.refetch();
    },
  };
}

export function useAdminAdvisorEmptySuggestionTexts(
  viteApiUrl: string | undefined,
  scope: string,
  organizationName: string | undefined,
  options: UseLaneUiTemplatesOptions,
): AgentLaneSuggestionChipsState {
  const label = organizationName ?? 'this organization';
  const enabled = options.enabled ?? true;
  const q = useAgentUiTemplatesQuery(viteApiUrl, { ...options, useCase: 'admin' });
  const suggestions = useMemo(() => {
    if (!q.data) return undefined;
    const scopeKey = scope === 'portfolio' ? 'portfolio' : 'organization';
    const sections = getAgentUiTemplateSections(q.data, 'admin', scopeKey);
    if (scopeKey === 'portfolio') {
      return resolveAgentUiTemplateSuggestionTexts(sections, {});
    }
    return resolveAgentUiTemplateSuggestionTexts(sections, { organization_label: label });
  }, [q.data, scope, label]);
  const resolvedTemplateRows = useMemo(() => {
    if (!q.data) return undefined;
    const scopeKey = scope === 'portfolio' ? 'portfolio' : 'organization';
    const sections = getAgentUiTemplateSections(q.data, 'admin', scopeKey);
    if (scopeKey === 'portfolio') {
      return resolveAgentUiTemplateRows(sections, {});
    }
    return resolveAgentUiTemplateRows(sections, { organization_label: label });
  }, [q.data, scope, label]);
  const isSuggestionsLoading = enabled && q.isFetching && q.data === undefined && !q.isError;
  return {
    suggestions,
    resolvedTemplateRows,
    isSuggestionsLoading,
    isSuggestionsError: q.isError,
    refetchSuggestions: () => {
      void q.refetch();
    },
  };
}

export function usePartnerAdvisorEmptySuggestionTexts(
  viteApiUrl: string | undefined,
  scope: string,
  organizationName: string | undefined,
  options: UseLaneUiTemplatesOptions,
): AgentLaneSuggestionChipsState {
  const label = organizationName ?? 'this client';
  const enabled = options.enabled ?? true;
  const q = useAgentUiTemplatesQuery(viteApiUrl, { ...options, useCase: 'partner' });
  const suggestions = useMemo(() => {
    if (!q.data) return undefined;
    const scopeKey = scope === 'portfolio' ? 'portfolio' : 'organization';
    const sections = getAgentUiTemplateSections(q.data, 'partner', scopeKey);
    if (scopeKey === 'portfolio') {
      return resolveAgentUiTemplateSuggestionTexts(sections, {});
    }
    return resolveAgentUiTemplateSuggestionTexts(sections, { organization_label: label });
  }, [q.data, scope, label]);
  const resolvedTemplateRows = useMemo(() => {
    if (!q.data) return undefined;
    const scopeKey = scope === 'portfolio' ? 'portfolio' : 'organization';
    const sections = getAgentUiTemplateSections(q.data, 'partner', scopeKey);
    if (scopeKey === 'portfolio') {
      return resolveAgentUiTemplateRows(sections, {});
    }
    return resolveAgentUiTemplateRows(sections, { organization_label: label });
  }, [q.data, scope, label]);
  const isSuggestionsLoading = enabled && q.isFetching && q.data === undefined && !q.isError;
  return {
    suggestions,
    resolvedTemplateRows,
    isSuggestionsLoading,
    isSuggestionsError: q.isError,
    refetchSuggestions: () => {
      void q.refetch();
    },
  };
}
